import { Component } from '@theme/component';

/**
 * BNO showcase card carousel.
 *
 * A purpose-built scroll-snap carousel (NOT the stock <slideshow-component>).
 * Native horizontal scrolling gives touch swipe, momentum and reduced-motion for
 * free; CSS scroll-snap centres the active card. This class adds:
 *   - active tracking: the card whose centre is nearest the track centre gets
 *     .is-active, and data-active-index on the host is kept in sync;
 *   - .is-visible on any card at least partly in the viewport (IntersectionObserver);
 *   - mouse drag-to-scroll (touch already scrolls natively);
 *   - keyboard: Left/Right arrows step between cards when the host has focus.
 *
 * No visual animation is applied to the card CONTENTS here; the card text is
 * always visible. The section-entry animation (bno-scroll.js) handles the first
 * appearance of the whole carousel when it scrolls into view.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} track
 * @property {HTMLElement[]} [card]
 * @extends {Component<Refs>}
 */
class BnoShowcaseCarousel extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.track = this.refs.track;
    this.cards = this.refs.card ?? [];

    // Fewer than two cards: no carousel behaviour. Still mark the single card so
    // the animation hooks are consistent.
    if (!this.track || this.cards.length < 2) {
      this.cards[0]?.classList.add('is-active', 'is-visible');
      return;
    }

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.activeIndex = 0;
    /** @type {number | null} */
    this.scrollRaf = null;

    // .is-visible for any card at least partly within the track's scrollport.
    this.io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          entry.target.classList.toggle('is-visible', entry.isIntersecting);
        }
      },
      { root: this.track, threshold: 0.01 }
    );
    for (const card of this.cards) this.io.observe(card);

    this.dots = this.refs.dot ?? [];
    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => this.goTo(i));
    });

    // Clicking a peeking (non-active) card centres it, like clicking its dot.
    // A click on the active card does nothing. A drag is NOT a click: the track's
    // capture-phase click blocker (in #setupDrag) stops the event before it
    // reaches these listeners when the pointer moved.
    this.cards.forEach((card, i) => {
      card.addEventListener('click', () => {
        if (i !== this.activeIndex) this.goTo(i);
      });
    });

    this.onScroll = this.onScroll.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.addEventListener('keydown', this.onKeydown);
    this.#setupDrag();

    this.updateActive();
    this.syncControls(this.activeIndex);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.io?.disconnect();
    this.track?.removeEventListener('scroll', this.onScroll);
    this.removeEventListener('keydown', this.onKeydown);
    if (this.scrollRaf) cancelAnimationFrame(this.scrollRaf);
  }

  onScroll() {
    if (this.scrollRaf) return;
    this.scrollRaf = requestAnimationFrame(() => {
      this.scrollRaf = null;
      this.updateActive();
    });
  }

  /** Active card = the one whose centre is nearest the track's viewport centre. */
  updateActive() {
    const trackRect = this.track.getBoundingClientRect();
    const centre = trackRect.left + trackRect.width / 2;

    let best = 0;
    let bestDist = Infinity;
    this.cards.forEach((card, i) => {
      const r = card.getBoundingClientRect();
      const dist = Math.abs(r.left + r.width / 2 - centre);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    });

    // .is-active on the centred card; .is-before / .is-after on cards to its
    // left / right (drives the directional hover cursor).
    this.cards.forEach((card, i) => {
      card.classList.toggle('is-active', i === best);
      card.classList.toggle('is-before', i < best);
      card.classList.toggle('is-after', i > best);
    });

    if (best !== this.activeIndex || this.getAttribute('data-active-index') !== String(best)) {
      this.activeIndex = best;
      this.setAttribute('data-active-index', String(best));
      this.syncControls(best);
    }
  }

  /**
   * Reflect the active index on the pagination dots (active dot = elongated
   * pill via .is-active) and mark it as the current one for assistive tech.
   * @param {number} index
   */
  syncControls(index) {
    this.dots?.forEach((dot, i) => {
      const active = i === index;
      dot.classList.toggle('is-active', active);
      if (active) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }

  /**
   * Scroll a card to centre.
   * @param {number} index
   */
  goTo(index) {
    const i = Math.max(0, Math.min(this.cards.length - 1, index));
    const card = this.cards[i];
    if (!card) return;

    const trackRect = this.track.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    const delta = cardRect.left + cardRect.width / 2 - (trackRect.left + trackRect.width / 2);
    const behavior = this.reducedMotion.matches ? 'auto' : 'smooth';
    this.track.scrollBy({ left: delta, behavior });
  }

  /** @param {KeyboardEvent} event */
  onKeydown(event) {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.goTo(this.activeIndex + 1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.goTo(this.activeIndex - 1);
    }
  }

  /** Mouse drag-to-scroll. Touch keeps native scrolling. */
  #setupDrag() {
    const track = this.track;
    let down = false;
    let startX = 0;
    let startScroll = 0;
    let moved = false;
    let captured = false;
    let pointerId = null;

    track.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      down = true;
      moved = false;
      captured = false;
      pointerId = e.pointerId;
      startX = e.clientX;
      startScroll = track.scrollLeft;
      // NOTE: do NOT capture the pointer here. Capturing on pointerdown makes the
      // browser dispatch the derived `click` to the capture target (this track)
      // instead of the card under the cursor, so clicking a peeking card never
      // reaches its click handler. Capture is deferred to pointermove, once an
      // actual drag begins.
    });

    track.addEventListener('pointermove', (e) => {
      if (!down) return;
      const dx = e.clientX - startX;
      if (!moved && Math.abs(dx) > 4) {
        // A real drag has started: now suspend snapping and capture the pointer
        // so the drag keeps tracking even outside the carousel.
        moved = true;
        track.style.scrollSnapType = 'none';
        try {
          track.setPointerCapture(pointerId);
          captured = true;
        } catch (_) {
          /* ignore */
        }
      }
      if (moved) track.scrollLeft = startScroll - dx;
    });

    const end = () => {
      if (!down) return;
      down = false;
      if (moved) {
        // Restore CSS snapping -> the track snaps to the nearest card.
        track.style.scrollSnapType = '';
        if (captured) {
          try {
            track.releasePointerCapture(pointerId);
          } catch (_) {
            /* ignore */
          }
        }
        // Drag is over: re-check the active card once the snap lands.
        requestAnimationFrame(() => this.updateActive());
      }
    };

    track.addEventListener('pointerup', end);
    track.addEventListener('pointercancel', end);
    // A drag must not fire a click on whatever was under the cursor. Plain clicks
    // (moved === false) pass through to the card click handler.
    track.addEventListener(
      'click',
      (e) => {
        if (moved) {
          e.preventDefault();
          e.stopPropagation();
        }
      },
      true
    );
    track.addEventListener('dragstart', (e) => e.preventDefault());
  }
}

if (!customElements.get('bno-showcase-carousel')) {
  customElements.define('bno-showcase-carousel', BnoShowcaseCarousel);
}
